import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { qk } from '../../lib/query'

/**
 * What this buyer asked before, as bubbles above the question being answered.
 *
 * Mercado Livre gives every question its own id and no thread, so a follow-up lands as a brand
 * new conversation with nothing before it. The buyer does not experience it that way: they
 * write "Agradeço pelas orientações" and add one more detail, expecting whoever reads it to
 * remember the rest.
 *
 * Rendered INSIDE the timeline rather than as a panel above it. The first attempt was a fixed
 * box, which added a fourth stacked header — reminder, product, history, notice — and left the
 * live question squeezed into whatever was left. History is conversation, so it scrolls away
 * like conversation. Faded, because it is already answered and already published.
 */

function dayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
}

export function QuestionHistory({ conversationId }: { conversationId: string }) {
  const { data } = useQuery({
    queryKey: qk.conversationHistory(conversationId),
    queryFn: () => api.conversationHistory(conversationId),
  })
  // Only the most recent couple by default. Someone opening a thread wants the exchange that
  // led to it, not sixteen questions — and this scrolls, so the rest is one click away.
  const [expanded, setExpanded] = useState(false)
  if (!data?.length) return null

  const shown = expanded ? data : data.slice(-2)
  const hidden = data.length - shown.length

  return (
    <>
      {hidden > 0 && (
        <div className="sep">
          <span className="ln" />
          <button className="lb as-btn" onClick={() => setExpanded(true)}>
            ver {hidden} pergunta{hidden > 1 ? 's' : ''} anterior{hidden > 1 ? 'es' : ''}
          </button>
          <span className="ln" />
        </div>
      )}

      {shown.map((t) => (
        <div key={t.conversationId} className="past-thread">
          <div className="sep">
            <span className="ln" />
            <span className="lb">
              {dayLabel(t.occurredAt)}
              {/* Saying which product is the whole point of showing it: an answer about another
                  listing is context, not an answer to this question. */}
              {!t.sameItem && (
                <span className="other-item" title={t.itemTitle ?? undefined}>
                  · outro anúncio{t.itemTitle ? `: ${t.itemTitle}` : ''}
                </span>
              )}
              {!t.answered && <span className="other-item">· sem resposta</span>}
            </span>
            <span className="ln" />
          </div>
          {t.messages.map((m, i) => (
            <div key={i} className={`row past ${m.direction === 'outgoing' ? 'out' : 'in'}`}>
              <div className="bub">{m.text}</div>
            </div>
          ))}
        </div>
      ))}

      {/* The boundary matters more than the history does: without it the agent cannot tell which
          question they are actually answering. */}
      <div className="sep now">
        <span className="ln" />
        <span className="lb">Pergunta atual</span>
        <span className="ln" />
      </div>
    </>
  )
}
